// 190 -- Edit a child's identity (name / DOB / nickname). Notify-only: saving
// keeps the child on every roster; a name/DOB change alerts their coaches.

import { useState } from "react";
import type { ParentPlayerPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export function EditChildDialog({
  child,
  onClose,
  onSaved,
}: {
  child: ParentPlayerPublic;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(child.first_name ?? "");
  const [surname, setSurname] = useState(child.surname ?? "");
  const [nickname, setNickname] = useState(child.nickname ?? "");
  const [dob, setDob] = useState(child.date_of_birth);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!firstName.trim() || !surname.trim() || !dob) {
      setError("First name, surname and date of birth are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.updateChild(child.player_id, {
        first_name: firstName.trim(),
        surname: surname.trim(),
        nickname: nickname.trim() || null,
        date_of_birth: dob,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save changes");
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="edit-child"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardHeader>
          <CardTitle>Edit {child.player_name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Changes save right away. If you change the name or date of birth, the coach is notified.
          </p>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ec-first">First name</Label>
              <Input id="ec-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-field="first-name" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ec-surname">Surname</Label>
              <Input id="ec-surname" value={surname} onChange={(e) => setSurname(e.target.value)} data-field="surname" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ec-nick">Nickname (optional)</Label>
              <Input id="ec-nick" value={nickname} onChange={(e) => setNickname(e.target.value)} data-field="nickname" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ec-dob">Date of birth</Label>
              <Input id="ec-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-field="dob" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving} data-action="save-child">
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
